/**
 * Hesap Aktarımı — bir masanın ADİSYONUNDAKİ (tüm açık siparişlerin)
 * kalemlerinden seçilenleri başka masaya taşır.
 *
 * Hem POS hem Admin tarafında kullanılır (shared). Senaryo: bir masada 10
 * sipariş × 50 kalem olabilir; bir grup başka masaya geçer. Garson tek "Hesap
 * Aktar" der, 50 kalemden ilgilileri (kısmi porsiyon dahil) seçer — hangi
 * siparişten geldiklerine bakılmaksızın hedef masada TEK hesapta birleşir.
 *
 * Kurallar:
 * - Ödenmiş satır disabled (iade karmaşıklığı yok)
 * - İkram/iadeli satır kısmen bölünemez (yalnız tamamı aktarılır)
 * - Çok porsiyonlu satırda kısmi miktar seçilebilir (21'den 4)
 * - Hedef masada açık Order varsa lines oraya eklenir, yoksa yeni açılır
 * - Kaynakta hiç satır kalmazsa sipariş iptal + masa boşalır
 */
import { useEffect, useState } from "react";
import {
  Modal, Stack, Checkbox, Text, Group, Button, Card, Badge, Select,
  Alert, Divider, NumberInput,
} from "@mantine/core";
import { IconAlertCircle, IconArrowsExchange } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useTables, useTransferSessionLines } from "../api";

function formatTRY(minor: number): string {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
  }).format((minor || 0) / 100);
}

type Line = {
  id: string;
  product_name: string;
  quantity: number;
  line_total_minor?: number;
  paid_amount_minor?: number;
  comp_quantity_approved?: number;
  returned_quantity_approved?: number;
};

// Adisyondaki bir sipariş — başlık (kısa id) + kalemleri.
type TransferOrder = {
  id: string;
  short_id?: string;
  kitchen_status?: string;
  lines: Line[];
};

export function TransferLinesModal({
  opened, onClose,
  sourceTableId, orders, onTransferred,
}: {
  opened: boolean;
  onClose: () => void;
  sourceTableId: string | null;
  // Adisyondaki tüm açık siparişler (kalemleriyle). Modal hepsini tek listede
  // sipariş başlıklarıyla gruplu gösterir.
  orders: TransferOrder[];
  onTransferred?: () => void;
}) {
  const transfer = useTransferSessionLines();
  const { data: tablesData } = useTables();
  const tables: any[] = tablesData?.results || tablesData || [];

  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [targetTableId, setTargetTableId] = useState<string | null>(null);
  // Kısmi aktarım: {lineId: aktarılacak qty}. Seçilen satır için varsayılan
  // tüm qty; kullanıcı azaltabilir (örn. 21'den 4).
  const [qtyById, setQtyById] = useState<Record<string, number | string>>({});

  // Modal her açıldığında durumu sıfırla
  useEffect(() => {
    if (opened) {
      setSelectedLineIds([]);
      setTargetTableId(null);
      setQtyById({});
    }
  }, [opened]);

  // Tüm siparişlerin kalemlerini düz liste — id→line hızlı erişim için.
  const allLines: Line[] = orders.flatMap((o) => o.lines || []);
  const lineById = new Map(allLines.map((l) => [l.id, l]));

  const isSelectable = (l: Line) => (l.paid_amount_minor ?? 0) === 0;
  const selectableCount = allLines.filter(isSelectable).length;

  // İkram/iadeli porsiyon kaynakta kalmalı → taşınabilir tavan = serbest qty.
  const lockedQty = (l: Line) =>
    (l.comp_quantity_approved ?? 0) + (l.returned_quantity_approved ?? 0);
  const freeQty = (l: Line) => Math.max(0, l.quantity - lockedQty(l));

  // Bir satır seçilince varsayılan aktarım qty'si = serbest porsiyon (ikram
  // kaynakta kalır). Kullanıcı bunu azaltabilir.
  const defaultQty = (l: Line) => freeQty(l);

  // Seçili tutar — kısmi qty oranında (line_total × seçili_qty / satır_qty).
  const selectedTotal = selectedLineIds.reduce((s, id) => {
    const l = lineById.get(id);
    if (!l) return s;
    const full = l.line_total_minor ?? 0;
    const q = Number(qtyById[id] ?? defaultQty(l));
    return s + Math.round((full * q) / (l.quantity || 1));
  }, 0);

  // Hedef masa seçenekleri — source masa hariç tüm masalar.
  const targetOptions = tables
    .filter((t) => String(t.id) !== String(sourceTableId))
    .map((t) => ({
      value: String(t.id),
      label: `${t.name}${t.state === "OCCUPIED" ? " (dolu)" : t.state === "FREE" ? " (boş)" : ""}`,
    }));

  function selectAll() {
    setSelectedLineIds(allLines.filter(isSelectable).map((l) => l.id));
  }

  async function handleTransfer() {
    if (selectedLineIds.length === 0) {
      notifications.show({ message: "En az bir kalem seçin", color: "red" });
      return;
    }
    if (!targetTableId || !sourceTableId) {
      notifications.show({ message: "Hedef masa seçin", color: "red" });
      return;
    }
    // Kısmi qty haritası — sadece satırın tamamından AZ seçilenler gönderilir.
    const lineQuantities: Record<string, number> = {};
    for (const id of selectedLineIds) {
      const l = lineById.get(id);
      if (!l) continue;
      const q = Number(qtyById[id] ?? defaultQty(l));
      // Tamamı (serbest = satır qty) değilse kısmi gönder. İkramlı satırda
      // free < quantity olduğu için her zaman kısmi gider (ikram kaynakta kalır).
      if (q > 0 && q < l.quantity) lineQuantities[id] = q;
    }
    try {
      const res = await transfer.mutateAsync({
        sourceTableId, targetTableId, lineIds: selectedLineIds,
        ...(Object.keys(lineQuantities).length ? { lineQuantities } : {}),
      });
      notifications.show({
        title: "✓ Aktarım başarılı",
        message: `${res.transferred_count} kalem ${res.target_table_name} masasına aktarıldı`,
        color: "green",
        autoClose: 5000,
      });
      onTransferred?.();
      onClose();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Aktarım başarısız",
        color: "red",
      });
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="🔀 Hesap Aktar"
      size="md"
      radius="md"
    >
      <Stack gap="md">
        <Alert color="blue" icon={<IconAlertCircle size={18} />}>
          Adisyondaki kalemlerden seçilenleri başka masaya taşır. Farklı
          siparişlerden seçebilirsiniz; hedef masada tek hesapta birleşir.
          Ödenmiş kalemler aktarılamaz; önce iade yapın.
        </Alert>

        <Divider label="Aktarılacak kalemler" labelPosition="center" />

        {selectableCount === 0 ? (
          <Alert color="orange" icon={<IconAlertCircle size={18} />}>
            Aktarılabilir kalem yok (hepsi ödenmiş veya boş).
          </Alert>
        ) : (
          <Stack gap={6}>
            <Group justify="space-between">
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={selectAll}
                disabled={selectedLineIds.length === selectableCount}
              >
                ✓ Tümünü Seç ({selectableCount})
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={() => setSelectedLineIds([])}
                disabled={selectedLineIds.length === 0}
              >
                Temizle
              </Button>
            </Group>

            {/* Sipariş başlığı + kalemleri gruplu. */}
            {orders.map((o, oi) => {
              const lines = o.lines || [];
              if (lines.length === 0) return null;
              return (
                <Stack key={o.id} gap={4}>
                  <Group gap={6}>
                    <Badge size="xs" variant="light" color="gray">
                      Sipariş #{oi + 1}
                    </Badge>
                    <Text size="xs" c="dimmed">
                      {o.short_id || o.id.slice(0, 8)}
                    </Text>
                  </Group>
                  {lines.map((l) => {
                    const lineTotal = l.line_total_minor ?? 0;
                    const isPaid = !isSelectable(l);
                    const isChecked = selectedLineIds.includes(l.id);
                    return (
                      <Card
                        key={l.id}
                        padding="xs"
                        radius="sm"
                        withBorder
                        style={{
                          cursor: isPaid ? "not-allowed" : "pointer",
                          opacity: isPaid ? 0.55 : 1,
                          borderColor: isChecked
                            ? "var(--mantine-color-blue-5)"
                            : undefined,
                          background: isChecked
                            ? "var(--mantine-color-blue-0)"
                            : undefined,
                        }}
                        onClick={() => {
                          if (isPaid) return;
                          setSelectedLineIds((prev) =>
                            isChecked
                              ? prev.filter((id) => id !== l.id)
                              : [...prev, l.id],
                          );
                        }}
                      >
                        <Group justify="space-between" wrap="nowrap">
                          <Group gap={8} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                            <Checkbox
                              checked={isChecked}
                              disabled={isPaid}
                              onChange={() => {}}
                              tabIndex={-1}
                              styles={{ input: { cursor: "pointer" } }}
                            />
                            <Text size="sm" fw={500} lineClamp={1}>
                              {l.quantity}× {l.product_name}
                            </Text>
                            {isPaid && (
                              <Badge size="xs" color="green">✓ Ödendi</Badge>
                            )}
                            {(l.comp_quantity_approved ?? 0) > 0 && (
                              <Badge size="xs" color="pink" variant="light">
                                🎁 {l.comp_quantity_approved} ikram (kalır)
                              </Badge>
                            )}
                            {(l.returned_quantity_approved ?? 0) > 0 && (
                              <Badge size="xs" color="indigo" variant="light">
                                ↩ {l.returned_quantity_approved} iade (kalır)
                              </Badge>
                            )}
                          </Group>
                          <Text size="sm" fw={600} style={{ flexShrink: 0 }}>
                            {formatTRY(lineTotal)}
                          </Text>
                        </Group>
                        {/* Kısmi miktar — seçili + birden fazla SERBEST porsiyon
                            varsa. İkramlı satırda max = serbest (ikram kaynakta
                            kalır). "21'den kaçını taşı? (max 20)" */}
                        {isChecked && freeQty(l) > 1 && (
                          <Group
                            gap={8}
                            mt={6}
                            wrap="nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                              Kaç porsiyon aktarılsın?
                            </Text>
                            <NumberInput
                              size="xs"
                              w={90}
                              min={1}
                              max={freeQty(l)}
                              allowDecimal={false}
                              value={qtyById[l.id] ?? defaultQty(l)}
                              onChange={(v) =>
                                setQtyById((prev) => ({ ...prev, [l.id]: v }))
                              }
                            />
                            <Text size="xs" c="dimmed">
                              / {freeQty(l)}
                              {lockedQty(l) > 0 && ` (${lockedQty(l)} ikram/iade kalır)`}
                            </Text>
                          </Group>
                        )}
                      </Card>
                    );
                  })}
                </Stack>
              );
            })}
          </Stack>
        )}

        <Group justify="space-between">
          <Text c="dimmed" size="sm">Seçili tutar</Text>
          <Text fw={700} c="blue">{formatTRY(selectedTotal)}</Text>
        </Group>

        <Divider label="Hedef masa" labelPosition="center" />
        <Select
          placeholder="Hangi masaya aktarılacak?"
          data={targetOptions}
          value={targetTableId}
          onChange={setTargetTableId}
          searchable
          comboboxProps={{ withinPortal: true, zIndex: 500 }}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button
            color="blue"
            leftSection={<IconArrowsExchange size={18} />}
            loading={transfer.isPending}
            disabled={selectedLineIds.length === 0 || !targetTableId}
            onClick={handleTransfer}
          >
            Aktar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
